import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight, BarChart3, Bell, CalendarDays, Crown, FileText, Search, UserRound, Users, WalletCards } from "lucide-react";
import { getPartnerDashboard } from "@/lib/partner.functions";
import { getMyProfile } from "@/lib/profile.functions";
import { getMyMembership } from "@/lib/memberships.functions";
import { gbp, sc } from "@/lib/format";
import { ShieldsLogo } from "@/components/brand/ShieldsLogo";
import { ShieldsLoader } from "@/components/brand/ShieldsLoader";
import { WorkspaceSwitcher } from "@/components/site/WorkspaceSwitcher";
import { PageShell, SectionHeader } from "@/components/ui/shields-layout";

const dashboardQuery=queryOptions({queryKey:["partner-dashboard"],queryFn:()=>getPartnerDashboard()});
const profileQuery=queryOptions({queryKey:["my-profile"],queryFn:()=>getMyProfile()});
const membershipQuery=queryOptions({queryKey:["my-membership"],queryFn:()=>getMyMembership()});

export const Route=createFileRoute("/_authenticated/partner/")({
  head:()=>({meta:[{title:"Business — Shields Partner"},{name:"description",content:"Your Shields Partner business home."},{name:"robots",content:"noindex"}]}),
  component:PartnerHome,pendingComponent:()=> <ShieldsLoader label="Opening your business"/>,
});

function PartnerHome(){
  const {data:stats}=useSuspenseQuery(dashboardQuery);
  const {data:profile}=useSuspenseQuery(profileQuery);
  const {data:membership}=useSuspenseQuery(membershipQuery);
  const firstName=profile.profile?.full_name?.trim().split(/\s+/)[0]||"Partner";
  const hour=new Date().getHours();
  const greeting=hour<12?"Good morning":hour<18?"Good afternoon":"Good evening";
  const now=new Date();
  const dayLabel=new Intl.DateTimeFormat("en-GB",{weekday:"short",day:"numeric",month:"short"}).format(now);
  const scheduleCount=stats.needsScheduling??0;
  const payoutNeedsAttention=Boolean(stats.payoutNeedsAttention);
  const attentionCount=(stats.activeOffers>0?1:0)+(scheduleCount>0?1:0)+(payoutNeedsAttention?1:0);
  const planName=membership?.plan?.name??(membership?.plan_slug==="partner-enterprise-v2"?"Shields Business Enterprise":membership?.plan_slug==="partner-business-v2"?"Shields Business":membership?.plan_slug==="partner-pro-v2"||membership?.plan_slug==="partner-pro"?"Shields Business Pro":"Shields Business Starter");
  const planCredits=membership?.plan?.monthly_credits??0;

  return <div className="partner-ref-shell">
    <header className="partner-ref-header">
      <Link to="/partner" className="partner-ref-brand"><ShieldsLogo/><span className="partner-ref-pill">Partner</span></Link>
      <div className="partner-ref-header-actions">
        <WorkspaceSwitcher fallbackLabel="Partner" />
        <Link to="/partner/notifications" aria-label="Notifications" className="partner-ref-round"><Bell/></Link>
        <Link to="/partner/profile" aria-label="Business profile" className="partner-ref-round">{profile.avatarUrl?<img src={profile.avatarUrl} alt=""/>:<UserRound/>}</Link>
      </div>
    </header>

    <PageShell width="standard" className="partner-ref-main">
      <section className="partner-ref-welcome">
        <div><h1>{greeting}, {firstName}</h1><p>Let’s get to work.</p></div>
        <Link to="/partner/business/calendar" className="partner-ref-date"><CalendarDays aria-hidden /><span><small>{dayLabel}</small><strong>Open diary</strong></span><ArrowRight aria-hidden /></Link>
      </section>

      <section className="partner-ref-grid">
        <HomeTile to="/partner/leads" icon={FileText} title="Cleaning leads" text="Review assigned home-care enquiries and submit prices" gold/>
        <HomeTile to="/partner/offers" icon={Search} title="Opportunities" text="See work matched to your business"/>
        <HomeTile to="/partner/business" icon={Users} title="Business tools" text="Customers, quotes, invoices and tools"/>
        <HomeTile to="/partner/profile" icon={UserRound} title="Business Profile" text="What customers see about your company"/>
        <HomeTile to="/partner/business/$module" params={{module:"analytics"}} icon={BarChart3} title="Reports & insights" text="See performance and profitability" gold/>
      </section>

      <section className="partner-ref-panel">
        <SectionHeader title="Needs your attention" action={attentionCount>0?<span className="partner-ref-badge">{attentionCount}</span>:undefined}/>
        <div className="partner-ref-attention">
          {stats.activeOffers>0&&<Attention to="/partner/offers" icon={CalendarDays} tone="red" title={stats.activeOffers+" new job opportunit"+(stats.activeOffers===1?"y":"ies")} text="Match your skills and coverage"/>}
          {scheduleCount>0&&<Attention to="/partner/jobs" icon={CalendarDays} tone="blue" title={scheduleCount+" job"+(scheduleCount===1?"":"s")+" needs scheduling"} text="Confirm the date and time"/>}
          {payoutNeedsAttention&&<Attention to="/partner/payments" search={{tab:"payouts"}} icon={WalletCards} tone="gold" title="Finish payout setup" text="Connect or complete your payout account"/>}
          {attentionCount===0&&<div className="partner-ref-caught">You’re all caught up.</div>}
        </div>
      </section>

      <section className="partner-ref-panel">
        <SectionHeader title="At a glance" action={<span className="partner-ref-period">Today & this month</span>}/>
        <div className="partner-ref-metrics">
          <Metric value={String(stats.todayJobs ?? 0)} label="Jobs today"/>
          <Metric value={String(stats.activeOffers)} label="Live opportunities"/>
          <Metric value={gbp(stats.earnedPence)} label="Completed revenue"/>
          <Metric value={sc(stats.balance)} label="Credits available"/>
        </div>
      </section>

      <section className="partner-ref-panel">
        <SectionHeader title="Quick actions"/>
        <div className="partner-ref-quick">
          <Quick to="/partner/offers" icon={FileText} label="View offers" gold/>
          <Quick to="/partner/business/$module" params={{module:"invoices"}} icon={FileText} label="Invoices"/>
          <Quick to="/partner/business/calendar" icon={CalendarDays} label="Plan diary"/>
          <Quick to="/partner/business/$module" params={{module:"customers"}} icon={Users} label="Customers"/>
          <Quick to="/partner/wallet" icon={WalletCards} label="Buy credits"/>
        </div>
        <Link to="/plans" className="partner-ref-membership">
          <span className="partner-ref-membership-icon"><Crown/></span>
          <span><strong>{planName}</strong><small>{planCredits>0?sc(planCredits)+" included each month · View benefits":"Build your business with more Shields benefits"}</small></span>
          <span className="partner-ref-plan-action">View plan <ArrowRight/></span>
        </Link>
      </section>
    </PageShell>
  </div>
}

function HomeTile({to,params,icon:Icon,title,text,gold=false}:{to:string;params?:any;icon:any;title:string;text:string;gold?:boolean}){return <Link to={to as any} params={params} className={"partner-ref-tile"+(gold?" is-gold":"")}><Icon/><span><strong>{title}</strong><small>{text}</small></span><ArrowRight className="partner-ref-arrow"/></Link>}
function Attention({to,search,icon:Icon,title,text,tone}:{to:string;search?:any;icon:any;title:string;text:string;tone:"red"|"blue"|"gold"}){return <Link to={to as any} search={search} className={"partner-ref-attention-row is-"+tone}><span><Icon/></span><div><strong>{title}</strong><small>{text}</small></div><ArrowRight/></Link>}
function Metric({value,label}:{value:string;label:string}){return <article className="partner-ref-metric"><strong>{value}</strong><small>{label}</small></article>}
function Quick({to,params,icon:Icon,label,gold=false}:{to:string;params?:any;icon:any;label:string;gold?:boolean}){return <Link to={to as any} params={params} className="partner-ref-quick-item"><span className={gold?"is-gold":""}><Icon/></span><small>{label}</small></Link>}
